import type { ApiTypes } from '@api/client';
import type { UserCurrent } from '@api/types';
import { dialogStore } from '@components/ui/dialog';
import { userStore } from '@components/user/store';
import type { TranslationFunctions } from '@i18n/i18n-types';
import { RoomClosedReason } from '@shared/admin/room';
import { MessageChangeReasons, RequiredStartStep } from '@shared/types';
import { dayjs } from '@utils/timestamps';

import type { LocalizedString } from 'typesafe-i18n';

export function getChatError(
    $LL: TranslationFunctions,
    reasons: NonNullable<ApiTypes.MessageReceived['replacementsReasons']>
): string | undefined {
    if (!reasons.length) {
        return;
    }

    if (
        reasons.includes(MessageChangeReasons.Links) &&
        reasons.includes(MessageChangeReasons.ProhibitedWords)
    ) {
        return $LL.chat.errors.containedProhibitedWordsAndLinks();
    } else if (reasons.includes(MessageChangeReasons.Links)) {
        return $LL.chat.errors.containedLinks();
    } else if (reasons.includes(MessageChangeReasons.ProhibitedWords)) {
        return $LL.chat.errors.containedProhibitedWords();
    }
}

export function filterDevices(devices: MediaDeviceInfo[], kind: MediaDeviceInfo['kind']) {
    const defaultSet = new Set();

    return devices
        .reduce<MediaDeviceInfo[]>((acc, device) => {
            if (device.kind === kind) {
                acc.push(device);

                if (device.deviceId === 'default') {
                    defaultSet.add(device.groupId);
                }
            }

            return acc;
        }, [])
        .filter((device) => device.deviceId === 'default' || !defaultSet.has(device.groupId));
}

export function getDefaultDevice(devices: MediaDeviceInfo[]): MediaDeviceInfo | null {
    return devices.find(item => item.deviceId === 'default') || devices[0];
}

export function getClosedReasonText(
    $LL: TranslationFunctions['roomClosedReason'],
    { user, closedReason, closedBy, isCall, endedAt }: {
        user: UserCurrent;
        endedAt: string;
        isCall: boolean;
        closedBy?: string;
        closedReason?: RoomClosedReason;
    },
    force = false): string {
    if (!force) {
        if (
            !dayjs(endedAt).isSame(dayjs(), 'day')
            || (closedBy === user.id && closedReason === RoomClosedReason.PressStop && !isCall)
        ) {
            return '';
        }
    }

    if (!closedReason) {
        return $LL.fallback();
    }

    const onClosedBy = (yourText: string, partnerText: string): string => {
        if (!closedBy) {
            return $LL.fallback();
        }
        return user.id !== closedBy ? partnerText : yourText;
    };

    if (isCall) {
        return user.operator
            ? onOperatorCall($LL, closedReason, onClosedBy)
            : onUserCall($LL, closedReason, onClosedBy);
    }
    return user.operator
        ? onOperatorSearch($LL, closedReason, onClosedBy)
        : onUserSearch($LL, closedReason, onClosedBy);
}

function onOperatorCall(
    $LL: TranslationFunctions['roomClosedReason'],
    closedReason: RoomClosedReason,
    onClosedBy: (yourText: string, partnerText: string) => string
): string {
    switch (closedReason) {
        case RoomClosedReason.PressStop:
        case RoomClosedReason.GoneAway:
            return onClosedBy($LL.youLeft(), $LL.userLeft());
        case RoomClosedReason.InvalidBalance:
            return $LL.userLeftBalance();
        case RoomClosedReason.PressStopBeforePlayerStarted:
        case RoomClosedReason.PressStopPlayerNotPlaying:
        case RoomClosedReason.GoneAwayBeforePlayerStarted:
        case RoomClosedReason.GoneAwayPlayerNotPlaying:
            return onClosedBy($LL.youLeftStreamingIssue(), $LL.userLeftStreamingIssue());
        case RoomClosedReason.StreamNotStarted:
        case RoomClosedReason.OperatorBroadcasterNotStarted:
        case RoomClosedReason.UserPlayerNotStarted:
        case RoomClosedReason.UserPlayerNotStartedTimeout:
        case RoomClosedReason.Timeout:
            return $LL.chatClosedStreamingIssue();
        case RoomClosedReason.AutoModerationStop:
        case RoomClosedReason.ModeratorStop:
        case RoomClosedReason.UserBlocked:
            return $LL.chatClosedViolationRules();
        case RoomClosedReason.SessionExists:
        case RoomClosedReason.CrossCallInterrupt:
            return onClosedBy($LL.chatClosedEngagedYou(), $LL.chatClosedEngagedUser());
        case RoomClosedReason.PermissionsRevoked:
            return onClosedBy($LL.chatClosedNoCameraYou(), $LL.chatClosedNoCameraUser());
        case RoomClosedReason.OfflineRoom:
            return $LL.chatClosedInactivity();
        default:
            return $LL.fallback();
    }
}

function onUserCall(
    $LL: TranslationFunctions['roomClosedReason'],
    closedReason: RoomClosedReason,
    onClosedBy: (yourText: string, partnerText: string) => string
): string {
    switch (closedReason) {
        case RoomClosedReason.PressStop:
        case RoomClosedReason.GoneAway:
            return onClosedBy($LL.youLeft(), $LL.userLeft());
        case RoomClosedReason.InvalidBalance:
            return $LL.youLeftBalance();
        case RoomClosedReason.PressStopBeforePlayerStarted:
        case RoomClosedReason.PressStopPlayerNotPlaying:
        case RoomClosedReason.GoneAwayBeforePlayerStarted:
        case RoomClosedReason.GoneAwayPlayerNotPlaying:
            return onClosedBy($LL.youLeftStreamingIssue(), $LL.userLeftStreamingIssue());
        case RoomClosedReason.StreamNotStarted:
        case RoomClosedReason.OperatorBroadcasterNotStarted:
        case RoomClosedReason.UserPlayerNotStarted:
        case RoomClosedReason.UserPlayerNotStartedTimeout:
        case RoomClosedReason.Timeout:
            return $LL.chatClosedStreamingIssue();
        case RoomClosedReason.SessionExists:
        case RoomClosedReason.CrossCallInterrupt:
            return onClosedBy($LL.chatClosedEngagedYou(), $LL.chatClosedEngagedUser());
        case RoomClosedReason.PermissionsRevoked:
            return onClosedBy($LL.chatClosedNoCameraYou(), $LL.chatClosedNoCameraUser());
        case RoomClosedReason.OfflineRoom:
            return $LL.chatClosedInactivity();
        default:
            return $LL.fallback();
    }
}

function onOperatorSearch(
    $LL: TranslationFunctions['roomClosedReason'],
    closedReason: RoomClosedReason,
    onClosedBy: (yourText: string, partnerText: string) => string
): string {
    switch (closedReason) {
        case RoomClosedReason.PressSkip:
        case RoomClosedReason.PressStop:
        case RoomClosedReason.GoneAway:
            return onClosedBy($LL.youLeft(), $LL.userLeft());
        case RoomClosedReason.InvalidBalance:
        case RoomClosedReason.FreeTimePackage:
            return $LL.userLeftBalance();
        case RoomClosedReason.PressSkipBeforePlayerStarted:
        case RoomClosedReason.PressSkipPlayerNotPlaying:
        case RoomClosedReason.PressStopBeforePlayerStarted:
        case RoomClosedReason.PressStopPlayerNotPlaying:
        case RoomClosedReason.GoneAwayBeforePlayerStarted:
        case RoomClosedReason.GoneAwayPlayerNotPlaying:
            return onClosedBy($LL.youLeftStreamingIssue(), $LL.userLeftStreamingIssue());
        case RoomClosedReason.StreamNotStarted:
        case RoomClosedReason.OperatorBroadcasterNotStarted:
        case RoomClosedReason.UserPlayerNotStarted:
        case RoomClosedReason.UserPlayerNotStartedTimeout:
        case RoomClosedReason.Timeout:
            return $LL.chatClosedStreamingIssue();
        case RoomClosedReason.AutoModerationStop:
        case RoomClosedReason.ModeratorStop:
        case RoomClosedReason.UserBlocked:
            return $LL.chatClosedViolationRules();
        case RoomClosedReason.SessionExists:
        case RoomClosedReason.CrossCallInterrupt:
            return onClosedBy($LL.chatClosedEngagedYou(), $LL.chatClosedEngagedUser());
        case RoomClosedReason.PermissionsRevoked:
            return onClosedBy($LL.chatClosedNoCameraYou(), $LL.chatClosedNoCameraUser());
        case RoomClosedReason.OfflineRoom:
            return $LL.chatClosedInactivity();
        default:
            return $LL.fallback();
    }
}

function onUserSearch(
    $LL: TranslationFunctions['roomClosedReason'],
    closedReason: RoomClosedReason,
    onClosedBy: (yourText: string, partnerText: string) => string
): string {
    switch (closedReason) {
        case RoomClosedReason.PressSkip:
        case RoomClosedReason.PressStop:
        case RoomClosedReason.GoneAway:
            return onClosedBy($LL.youLeft(), $LL.userLeft());
        case RoomClosedReason.InvalidBalance:
        case RoomClosedReason.FreeTimePackage:
            return $LL.youLeftBalance();
        case RoomClosedReason.PressSkipBeforePlayerStarted:
        case RoomClosedReason.PressSkipPlayerNotPlaying:
        case RoomClosedReason.PressStopBeforePlayerStarted:
        case RoomClosedReason.PressStopPlayerNotPlaying:
        case RoomClosedReason.GoneAwayBeforePlayerStarted:
        case RoomClosedReason.GoneAwayPlayerNotPlaying:
            return onClosedBy($LL.youLeftStreamingIssue(), $LL.userLeftStreamingIssue());
        case RoomClosedReason.StreamNotStarted:
        case RoomClosedReason.OperatorBroadcasterNotStarted:
        case RoomClosedReason.UserPlayerNotStarted:
        case RoomClosedReason.UserPlayerNotStartedTimeout:
        case RoomClosedReason.Timeout:
            return $LL.chatClosedStreamingIssue();
        case RoomClosedReason.SessionExists:
        case RoomClosedReason.CrossCallInterrupt:
            return onClosedBy($LL.chatClosedEngagedYou(), $LL.chatClosedEngagedUser());
        case RoomClosedReason.PermissionsRevoked:
            return onClosedBy($LL.chatClosedNoCameraYou(), $LL.chatClosedNoCameraUser());
        case RoomClosedReason.OfflineRoom:
            return $LL.chatClosedInactivity();
        default:
            return $LL.fallback();
    }
}

export function checkStepStartRequired(params: {
    onSuccess?: () => void;
    isHaveCloseBtn?: boolean;
    selfInfo?: {
        submitButtonText?: LocalizedString;
        subTitle?: LocalizedString;
    };
}): boolean {
    const user = userStore.getState().user;
    if (!user) {
        return false;
    }

    const stepStartRequiredData = user.stepStartRequiredData;
    const isOperator = Boolean(user.operator);
    if (stepStartRequiredData === RequiredStartStep.UserAgreement) {
        dialogStore.showUserAgreement({
            onAccept: () => {
                if (checkStepStartRequired(params) && params.onSuccess) {
                    params.onSuccess();
                }
            },
            isOperator,
        }, params.isHaveCloseBtn ?? true);
        return false;
    }

    if (stepStartRequiredData === RequiredStartStep.SpokenLanguages) {
        dialogStore.showSelfInfo({
            onSuccess: params.onSuccess ?? (() => { /* nothing */ }),
            submitButtonText: params.selfInfo?.submitButtonText,
            subTitle: params.selfInfo?.subTitle,
        }, params.isHaveCloseBtn ?? true);
        return false;
    }

    return true;
}
